import axios from 'axios';
import { auth } from './firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import * as Sentry from '@sentry/react';

export const DEV_TOKEN_KEY = 'dev_auth_token';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': 'pl',
  },
});

let currentUserPromise: Promise<any> | null = null;

const getCurrentUser = () => {
  if (!currentUserPromise) {
    currentUserPromise = new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe();
        resolve(user);
      });
    });
  }
  return currentUserPromise;
};

axiosInstance.interceptors.request.use(
  async (config) => {
    if (config.url?.includes('/auth/sign-in') || config.url?.includes('/auth/forgot-password')) {
      return config;
    }

    try {
      const user = await getCurrentUser();

      if (user) {
        const token = await user.getIdToken(true);
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting token:', error);
      Sentry.captureException(error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest.headers?.Authorization &&
      !originalRequest.url?.includes('/auth/forgot-password')
    ) {
      currentUserPromise = null;
      try {
        await signOut(auth);
      } catch (signOutError) {
        Sentry.captureException(signOutError);
      }
      localStorage.removeItem('auth');
      localStorage.removeItem('ra_token');
      localStorage.removeItem('ra_uid');
      localStorage.removeItem('ra_role');
      Sentry.setUser(null);
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const user = auth.currentUser;

        if (user) {
          const newToken = await user.getIdToken(true);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        console.error('Error refreshing token:', refreshError);
        Sentry.captureException(refreshError);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

auth.onAuthStateChanged((user) => {
  if (!user) {
    currentUserPromise = null;
  }
});

export default axiosInstance;
